// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import {RoleName} from '@app/features/guild/components/RoleName';
import type {GuildRole} from '@app/features/guild/models/GuildRole';
import Guilds from '@app/features/guild/state/Guilds';
import {roleNameToPlaintext} from '@app/features/guild/utils/RoleNameText';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as RoleOfferCommands from '@app/features/messaging/commands/RoleOfferCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {useRoleHierarchy} from '@app/features/permissions/hooks/useRoleHierarchy';
import Permission from '@app/features/permissions/state/Permission';
import * as ColorUtils from '@app/features/theme/utils/ColorUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {ElevatedPermissions, Permissions} from '@fluxer/constants/src/ChannelConstants';
import {MAX_ROLE_OFFERS_PER_MESSAGE} from '@fluxer/constants/src/LimitConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CheckIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo, useState} from 'react';
import styles from './RoleOffersModal.module.css';

const ROLE_SELECTORS_TITLE_DESCRIPTOR = msg({
	message: 'Role selectors',
	comment: 'Title of the modal for choosing self-assignable roles on a message.',
});
const ROLE_SELECTORS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Choose which roles people can assign themselves from this message. Up to {maxRoles}.',
	comment: 'Helper text in the role-selectors modal. Preserve {maxRoles}.',
});
const CLEAR_DESCRIPTOR = msg({
	message: 'Clear',
	comment: 'Button that removes all role offers from a message.',
});
const SEND_DESCRIPTOR = msg({
	message: 'Send',
	comment: 'Confirm button that sends a message with selected role offers.',
});
const SAVE_DESCRIPTOR = msg({
	message: 'Save',
	comment: 'Confirm button that saves role offers on an existing message.',
});
const NO_ELIGIBLE_ROLES_DESCRIPTOR = msg({
	message: 'No roles you can offer. Roles must sit below yours and must not include elevated permissions.',
	comment: 'Empty state when Manage Roles user has no offerable roles.',
});

function roleHasElevatedPermissions(permissions: bigint): boolean {
	return (permissions & ElevatedPermissions) !== 0n;
}

function getOfferableRoles(guildId: string, canManageRole: (role: GuildRole) => boolean): Array<GuildRole> {
	const guild = Guilds.getGuild(guildId);
	if (!guild) {
		return [];
	}
	const currentUserId = Authentication.currentUserId;
	const isOwner = currentUserId != null && guild.isOwner(currentUserId);
	return Guilds.getGuildRoles(guildId, false)
		.filter((role) => !role.isEveryone)
		.filter((role) => !roleHasElevatedPermissions(role.permissions))
		.filter((role) => isOwner || canManageRole(role))
		.sort((a, b) => b.position - a.position || a.id.localeCompare(b.id));
}

interface RoleOffersModalProps {
	guildId: string;
	initialRoleIds: ReadonlyArray<string>;
	mode: 'send' | 'edit';
	messageContent?: string;
	channelId?: string;
	message?: Message;
}

export const RoleOffersModal = observer(function RoleOffersModal({
	guildId,
	initialRoleIds,
	mode,
	messageContent,
	channelId,
	message,
}: RoleOffersModalProps) {
	const {i18n} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const {canManageRole} = useRoleHierarchy(guild);
	const offerableRoles = useMemo(() => getOfferableRoles(guildId, canManageRole), [canManageRole, guildId]);
	const [selected, setSelected] = useState<Set<string>>(() => new Set(initialRoleIds));
	const [submitting, setSubmitting] = useState(false);

	const toggleRole = useCallback((roleId: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(roleId)) {
				next.delete(roleId);
			} else if (next.size < MAX_ROLE_OFFERS_PER_MESSAGE) {
				next.add(roleId);
			}
			return next;
		});
	}, []);

	const close = useCallback(() => {
		ModalCommands.pop();
	}, []);

	const handleSave = useCallback(async () => {
		if (submitting) {
			return;
		}
		setSubmitting(true);
		try {
			const roleOffers = offerableRoles.filter((role) => selected.has(role.id)).map((role) => role.id);
			if (mode === 'send' && channelId != null && messageContent != null) {
				await RoleOfferCommands.sendMessageWithRoleOffers(channelId, messageContent, roleOffers);
			} else if (mode === 'edit' && message != null) {
				await RoleOfferCommands.setMessageRoleOffers(message, roleOffers);
			}
			close();
		} finally {
			setSubmitting(false);
		}
	}, [channelId, close, message, messageContent, mode, offerableRoles, selected, submitting]);

	const handleClear = useCallback(async () => {
		if (submitting || mode !== 'edit' || message == null) {
			return;
		}
		setSubmitting(true);
		try {
			await RoleOfferCommands.setMessageRoleOffers(message, []);
			close();
		} finally {
			setSubmitting(false);
		}
	}, [close, message, mode, submitting]);

	return (
		<Modal.Root size="small" centered data-flx="messaging.role-offers-modal.root">
			<Modal.Header title={i18n._(ROLE_SELECTORS_TITLE_DESCRIPTOR)} data-flx="messaging.role-offers-modal.header" />
			<Modal.Content data-flx="messaging.role-offers-modal.content">
				<Modal.ContentLayout data-flx="messaging.role-offers-modal.content-layout">
					<Modal.Description data-flx="messaging.role-offers-modal.description">
						{i18n._(ROLE_SELECTORS_DESCRIPTION_DESCRIPTOR, {maxRoles: MAX_ROLE_OFFERS_PER_MESSAGE})}
					</Modal.Description>
					{offerableRoles.length === 0 ? (
						<p className={styles.empty} data-flx="messaging.role-offers-modal.empty">
							{i18n._(NO_ELIGIBLE_ROLES_DESCRIPTOR)}
						</p>
					) : (
						<ul className={styles.roleList} data-flx="messaging.role-offers-modal.role-list">
							{offerableRoles.map((role) => {
								const checked = selected.has(role.id);
								const disabled = !checked && selected.size >= MAX_ROLE_OFFERS_PER_MESSAGE;
								return (
									<li key={role.id} className={styles.roleRow} data-flx="messaging.role-offers-modal.role-row">
										<FocusRing offset={-2} data-flx="messaging.role-offers-modal.focus-ring">
											<button
												type="button"
												className={clsx(styles.roleToggle, checked && styles.roleToggleSelected)}
												aria-pressed={checked}
												aria-label={roleNameToPlaintext(role.name)}
												disabled={disabled || submitting}
												onClick={() => toggleRole(role.id)}
												data-flx="messaging.role-offers-modal.role-toggle"
											>
												<span
													className={styles.roleIndicator}
													style={{backgroundColor: ColorUtils.int2rgb(role.color)}}
													data-flx="messaging.role-offers-modal.role-indicator"
												/>
												<span className={styles.roleName} data-flx="messaging.role-offers-modal.role-name">
													<RoleName name={role.name} data-flx="messaging.role-offers-modal.role-name" />
												</span>
												{checked ? (
													<CheckIcon
														className={styles.checkIcon}
														weight="bold"
														data-flx="messaging.role-offers-modal.check-icon"
													/>
												) : null}
											</button>
										</FocusRing>
									</li>
								);
							})}
						</ul>
					)}
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer data-flx="messaging.role-offers-modal.footer">
				{mode === 'edit' ? (
					<Button
						variant="secondary"
						onClick={() => void handleClear()}
						disabled={submitting || initialRoleIds.length === 0}
						data-flx="messaging.role-offers-modal.button"
					>
						{i18n._(CLEAR_DESCRIPTOR)}
					</Button>
				) : null}
				<Button
					variant="secondary"
					onClick={close}
					disabled={submitting}
					data-flx="messaging.role-offers-modal.button.close"
				>
					{i18n._(CANCEL_DESCRIPTOR)}
				</Button>
				<Button
					variant="primary"
					onClick={() => void handleSave()}
					disabled={submitting || (mode === 'send' && selected.size === 0) || offerableRoles.length === 0}
					data-flx="messaging.role-offers-modal.button--2"
				>
					{i18n._(mode === 'send' ? SEND_DESCRIPTOR : SAVE_DESCRIPTOR)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});

export function openRoleOffersSendModal(params: {guildId: string; channelId: string; messageContent: string}): void {
	if (!Permission.can(Permissions.MANAGE_ROLES, {guildId: params.guildId})) {
		return;
	}
	ModalCommands.push(
		modal(() => (
			<RoleOffersModal
				guildId={params.guildId}
				channelId={params.channelId}
				messageContent={params.messageContent}
				initialRoleIds={[]}
				mode="send"
				data-flx="messaging.role-offers-modal.open-role-offers-send-modal.role-offers-modal"
			/>
		)),
	);
}

export function openRoleOffersEditModal(message: Message): void {
	const guildId = Channels.getChannel(message.channelId)?.guildId ?? message.guildId;
	if (!guildId) {
		return;
	}
	if (!Permission.can(Permissions.MANAGE_ROLES, {guildId})) {
		return;
	}
	ModalCommands.push(
		modal(() => (
			<RoleOffersModal
				guildId={guildId}
				message={message}
				initialRoleIds={message.roleOffers}
				mode="edit"
				data-flx="messaging.role-offers-modal.open-role-offers-edit-modal.role-offers-modal"
			/>
		)),
	);
}
